import { useEffect } from 'react';

import { BpkButtonV2 } from '@skyscanner/backpack-web/bpk-component-button';
import BpkCard from '@skyscanner/backpack-web/bpk-component-card';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import { withI18n } from '../../contexts/i18n';
import { withMiniEventTracker } from '../../contexts/mini-events';
import { RewardPriceImage, RewardPriceDetailImage } from '../img';

import type { TranslationKey } from '@shared/constants/translations';
import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { I18nService } from '@shared/types/i18n';

import STYLES from './RewardPriceBanner.module.scss';

import { ACTION_TYPE } from '@shared/utils-frontend/mini-events/constants';

const cls = cssModules(STYLES);

type Props = {
  i18n: I18nService;
  showButton?: boolean;
  onButtonClick?: () => void;
  messageType: 'applied' | 'default' | 'detail';
  miniEventTracker: MiniEventTracker;
};

type BannerTexts = {
  [key: string]: TranslationKey;
  bannerTitle_default: TranslationKey;
  bannerTitle_applied: TranslationKey;
  bannerTitle_detail: TranslationKey;
  bannerDesc_default: TranslationKey;
  bannerDesc_applied: TranslationKey;
  bannerDesc_detail: TranslationKey;
  bannerButton: TranslationKey;
};

const rewardPriceBannerTexts: BannerTexts = {
  bannerTitle_default: 'Dayview_Reward_price_deals_banner_title',
  bannerTitle_applied: 'Dayview_Reward_price_deals_banner_title_applied',
  bannerTitle_detail: 'Dayview_Reward_price_deals_banner_title_detail',
  bannerDesc_default: 'Dayview_Reward_price_deals_banner_desc',
  bannerDesc_applied: 'Dayview_Reward_price_deals_banner_desc_applied',
  bannerDesc_detail: 'Dayview_Reward_price_deals_banner_desc_detail',
  bannerButton: 'Dayview_Reward_price_deals_banner_claim',
};

const RewardPriceBanner = ({
  i18n: { translate },
  messageType,
  miniEventTracker,
  onButtonClick,
  showButton = false,
}: Props) => {
  const showBanner = messageType !== 'detail';
  useEffect(() => {
    if (showBanner) {
      miniEventTracker.trackHotelsAction(
        ACTION_TYPE.DAY_VIEW_HOTEL_REWARD_PRICE_BANNER_LOADED,
      );
    }
  }, [miniEventTracker, showBanner]);

  const onBannerClick = () => {
    if (onButtonClick) {
      miniEventTracker.trackHotelsAction(
        ACTION_TYPE.DAY_VIEW_HOTEL_REWARD_PRICE_BANNER_CLICKED,
      );
      onButtonClick();
    }
  };

  if (!showBanner) {
    return null;
  }

  return (
    <BpkCard
      className={cls(
        'RewardPriceBanner',
        `${showBanner ? 'RewardPriceBanner__forDetail' : ''}`,
      )}
      atomic={false}
      padded={false}
    >
      <div className={cls('RewardPriceBanner__left')}>
        <div className={cls('RewardPriceBanner__description')}>
          <BpkText
            tagName="p"
            textStyle={TEXT_STYLES.heading4}
            className={cls('RewardPriceBanner__text')}
          >
            {translate(rewardPriceBannerTexts[`bannerTitle_${messageType}`])}
          </BpkText>
          <BpkText
            tagName="p"
            textStyle={TEXT_STYLES.bodyDefault}
            className={cls('RewardPriceBanner__text')}
          >
            {translate(rewardPriceBannerTexts[`bannerDesc_${messageType}`])}
          </BpkText>
        </div>
        {showButton && (
          <BpkButtonV2
            className={cls('RewardPriceBanner__button')}
            onClick={onBannerClick}
            aria-label={translate(rewardPriceBannerTexts.bannerButton)}
          >
            {translate(rewardPriceBannerTexts.bannerButton)}
          </BpkButtonV2>
        )}
      </div>
      <img
        src={showBanner ? RewardPriceDetailImage : RewardPriceImage}
        alt="Reward Price Banner"
        aria-hidden
        className={cls(
          'RewardPriceBanner__image',
          !showButton && 'RewardPriceBanner__samllImage',
        )}
      />
    </BpkCard>
  );
};

export default withMiniEventTracker(withI18n(RewardPriceBanner));
